'use client'

import { useState } from 'react'
import { AlertTriangle, Trash2, AlertCircle, CheckCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'

interface ConfirmationDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  description: string
  confirmText?: string
  cancelText?: string
  variant?: 'default' | 'warning' | 'danger' | 'info' | 'success'
  isLoading?: boolean
  /** If provided, user must type this word (case-insensitive) to enable the confirm button */
  confirmationWord?: string
}

/**
 * ConfirmationDialog
 *
 * A reusable confirmation dialog with icon variants.
 * Built on top of shadcn Dialog (closes on outside click).
 *
 * Variants:
 * - default: Primary color, no icon
 * - warning: Yellow/amber with warning icon
 * - danger: Red with trash icon
 * - info: Blue with info icon
 * - success: Green with checkmark icon
 */
export default function ConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  isLoading = false,
  confirmationWord,
}: ConfirmationDialogProps) {
  const [confirmationInput, setConfirmationInput] = useState('')

  // Handle dialog close with input reset
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setConfirmationInput('')
    }
    onClose()
  }

  const isConfirmButtonDisabled = confirmationWord
    ? confirmationInput.toLowerCase() !== confirmationWord.toLowerCase()
    : false

  const variantStyles = {
    default: {
      icon: null,
      iconColor: '',
      iconBgColor: '',
      buttonBgColor: '',
      buttonHoverBgColor: '',
      buttonTextColor: '',
    },
    warning: {
      icon: AlertTriangle,
      iconColor: '#f59e0b', // amber-500
      iconBgColor: 'rgba(245, 158, 11, 0.1)',
      buttonBgColor: '#f59e0b',
      buttonHoverBgColor: '#d97706',
      buttonTextColor: '#ffffff',
    },
    danger: {
      icon: Trash2,
      iconColor: '#ef4444', // red-500
      iconBgColor: 'rgba(239, 68, 68, 0.1)',
      buttonBgColor: '#ef4444',
      buttonHoverBgColor: '#dc2626',
      buttonTextColor: '#ffffff',
    },
    info: {
      icon: AlertCircle,
      iconColor: '#3b82f6', // blue-500
      iconBgColor: 'rgba(59, 130, 246, 0.1)',
      buttonBgColor: '#3b82f6',
      buttonHoverBgColor: '#2563eb',
      buttonTextColor: '#ffffff',
    },
    success: {
      icon: CheckCircle,
      iconColor: '#10b981', // emerald-500
      iconBgColor: 'rgba(16, 185, 129, 0.1)',
      buttonBgColor: '#10b981',
      buttonHoverBgColor: '#059669',
      buttonTextColor: '#ffffff',
    },
  }

  const style = variantStyles[variant]
  const Icon = style.icon

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="text-center sm:text-center">
          {/* Icon */}
          {Icon && (
            <div
              className="mx-auto flex items-center justify-center w-12 h-12 rounded-full mb-2"
              style={{ backgroundColor: style.iconBgColor }}
            >
              <Icon className="w-6 h-6" style={{ color: style.iconColor }} />
            </div>
          )}
          <DialogTitle className="text-center">{title}</DialogTitle>
          <DialogDescription className="text-center whitespace-pre-line">
            {description}
          </DialogDescription>
        </DialogHeader>

        {/* Confirmation Word Input */}
        {confirmationWord && (
          <div className="mt-4">
            <label htmlFor="confirmation-input" className="sr-only">
              Type &quot;{confirmationWord}&quot; to enable
            </label>
            <Input
              id="confirmation-input"
              placeholder={`Type "${confirmationWord}" to enable`}
              value={confirmationInput}
              onChange={(e) => setConfirmationInput(e.target.value)}
              className="w-full text-center"
            />
          </div>
        )}

        <DialogFooter className="sm:justify-center gap-3 mt-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 sm:flex-initial touch-manipulation"
          >
            {cancelText}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isLoading || isConfirmButtonDisabled}
            className="flex-1 sm:flex-initial touch-manipulation transition-colors"
            style={
              style.buttonBgColor
                ? {
                    backgroundColor: style.buttonBgColor,
                    color: style.buttonTextColor,
                  }
                : undefined
            }
            onMouseEnter={(e) => {
              if (style.buttonHoverBgColor) {
                e.currentTarget.style.backgroundColor = style.buttonHoverBgColor
              }
            }}
            onMouseLeave={(e) => {
              if (style.buttonBgColor) {
                e.currentTarget.style.backgroundColor = style.buttonBgColor
              }
            }}
          >
            {isLoading ? 'Loading...' : confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
